import React, { useRef, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { DropdownOption, DropdownOptionOrGroup, DropdownProps } from "../../types/screensTypes";
import styles from "./Dropdown.module.css";

export const getCustomStyles = (isError = false, { isMulti = false }: { isMulti?: boolean } = {}) => ({
    control: (base: any, state: any) => ({
        ...base,
        borderRadius: "var(--radius-sm, 7px)",
        fontSize: "15px",
        minHeight: isMulti ? "48px" : "46px",
        ...(isMulti ? { height: "auto", alignItems: "flex-start" } : {}),
        border: isError
            ? "1px solid var(--danger, #ff0000)"
            : state.isDisabled
              ? "1px solid var(--line, #e0e0e0)"
              : state.isFocused
                ? "1px solid var(--green-500, #86e3ce)"
                : `1px solid var(--line, ${base.borderColor || "#ccc"})`,
        boxShadow: state.isDisabled ? "none" : state.isFocused ? "0 0 0 3px var(--green-100, #ebfffa)" : "none",
        backgroundColor: state.isDisabled ? "var(--surface-2, #f5f5f5)" : "var(--surface, #fff)",
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        opacity: 1,
        "&:hover": {
            borderColor: state.isDisabled
                ? "var(--line, #e0e0e0)"
                : isError
                  ? "var(--danger, #ff0000)"
                  : "var(--ink-4, #999)",
        },
    }),
    option: (base: any, state: any) => ({
        ...base,
        fontSize: "14px",
        borderRadius: "var(--radius-xs, 5px)",
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        padding: "10px 12px",
        backgroundColor: state.isDisabled
            ? "transparent"
            : state.isSelected
              ? "var(--green-50, #ebfffa)"
              : state.isFocused
                ? "var(--green-50, #ebfffa)"
                : "transparent",
        color: state.isDisabled
            ? "var(--ink-4, #999)"
            : state.isSelected || state.isFocused
              ? "var(--green-800, #1d2f2b)"
              : "var(--ink, #333)",
        fontWeight: state.isSelected ? 600 : 400,
        opacity: state.isDisabled ? 0.55 : 1,
        "&:active": {
            backgroundColor: state.isDisabled ? "transparent" : "var(--green-100, #ebfffa)",
            color: state.isDisabled ? "var(--ink-4, #999)" : "var(--green-800, #1d2f2b)",
        },
    }),
    groupHeading: (base: any) => ({
        ...base,
        fontSize: "10.5px",
        fontWeight: 700,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color: "var(--ink-4, #999)",
        padding: "8px 12px 4px",
        margin: 0,
    }),
    menu: (base: any) => ({
        ...base,
        zIndex: 11000,
        borderRadius: "var(--radius-sm, 7px)",
        border: "1px solid var(--line)",
        boxShadow: "var(--shadow-md, 0 10px 30px rgba(0,0,0,.12))",
        overflow: "hidden",
    }),
    menuList: (base: any) => ({
        ...base,
        maxHeight: "200px",
        padding: "6px",
    }),
    menuPortal: (base: any) => ({
        ...base,
        zIndex: 11000,
    }),
    placeholder: (base: any, state: any) => ({
        ...base,
        fontSize: "15px",
        fontFamily: "var(--font-body, inherit)",
        color: state.isDisabled ? "var(--ink-4, #999)" : isError ? "var(--danger, #ff0000)" : "var(--ink-4, #999)",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: "100%",
    }),
    singleValue: (base: any, state: any) => ({
        ...base,
        fontSize: "15px",
        fontFamily: "var(--font-body, inherit)",
        color: state.isDisabled ? "var(--ink-4, #999)" : isError ? "var(--danger, #ff0000)" : "var(--ink, #333)",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        maxWidth: "100%",
    }),
    multiValue: (base: any) => ({
        ...base,
        fontSize: "13px",
        fontFamily: "var(--font-body, inherit)",
        backgroundColor: isError ? "#ffe6e6" : "var(--surface-2, #f5f5f5)",
        border: isError ? "1px solid var(--danger, #ff0000)" : "1px solid var(--line)",
        borderRadius: "7px",
        color: isError ? "var(--danger, #ff0000)" : "var(--ink, #333)",
        margin: 0,
        minHeight: 0,
        minWidth: 0,
        maxWidth: "100%",
        alignItems: "center",
        overflow: "hidden",
    }),
    multiValueLabel: (base: any) => ({
        ...base,
        color: isError ? "var(--danger, #ff0000)" : "var(--ink, #333)",
        fontSize: "13px",
        fontWeight: 600,
        padding: "3px 2px 3px 9px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        minWidth: 0,
        flex: "1 1 auto",
    }),
    multiValueRemove: (base: any) => ({
        ...base,
        color: "var(--ink-3)",
        borderRadius: 0,
        paddingLeft: "5px",
        paddingRight: "7px",
        ":hover": {
            backgroundColor: "transparent",
            color: "var(--ink)",
        },
    }),
    valueContainer: (base: any) => ({
        ...base,
        flexWrap: isMulti ? "wrap" : "nowrap",
        overflow: isMulti ? "visible" : "hidden",
        gap: isMulti ? "6px" : undefined,
        /* Shorthand replaces react-select base `padding` so longhands can't lose the cascade */
        padding: isMulti ? "7px 8px 7px 12px" : "2px 8px 2px 14px",
        alignItems: "center",
        ...(isMulti ? { position: "relative" as const } : {}),
    }),
    input: (base: any) => ({
        ...base,
        margin: 0,
        padding: 0,
        ...(isMulti
            ? {
                  height: "auto",
                  minHeight: 0,
                  maxWidth: "100%",
              }
            : {}),
    }),
    ...(isMulti
        ? {
              inputContainer: (base: any) => ({
                  ...base,
                  margin: 0,
                  padding: 0,
                  flex: "1 1 auto",
                  minWidth: 2,
                  maxWidth: "100%",
              }),
          }
        : {}),
    dropdownIndicator: (base: any, state: any) => ({
        ...base,
        color: "var(--ink-4, #999)",
        padding: "8px",
        flex: "none",
        opacity: state.isDisabled ? 0.55 : 1,
        "&:hover": {
            color: state.isDisabled ? "var(--ink-4, #999)" : "var(--ink-2, #666)",
        },
    }),
    indicatorSeparator: () => ({
        display: "none",
    }),
    clearIndicator: (base: any) => ({
        ...base,
        color: "var(--ink-4, #999)",
        padding: "8px",
        cursor: "pointer",
        "&:hover": {
            color: "var(--ink-2, #666)",
        },
    }),
});

const Dropdown: React.FC<DropdownProps & { className?: string }> = ({
    label,
    options,
    onChange,
    loading,
    error,
    disabled = false,
    value = "",
    placeholder = "Select an option",
    isMissing = false,
    isWithoutMargin = false,
    isClearable = false,
    onBlur,
    name,
    className = "",
    fullWidth = false,
    maxWidth = "none",
    classes = {},
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const containerRef = useRef<HTMLDivElement>(null);
    const [placement, setPlacement] = useState<"bottom" | "top">("bottom");

    const MENU_MAX_HEIGHT = 200;
    const MENU_BUFFER = 20;

    const handleMenuOpen = useCallback(() => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            setPlacement(spaceBelow < MENU_MAX_HEIGHT + MENU_BUFFER ? "top" : "bottom");
        }
    }, []);

    const widthClass = fullWidth ? (maxWidth === "form" ? styles.fullWidthForm : styles.fullWidth) : "";

    const rootClass = [
        isWithoutMargin ? `${styles.withoutMargin} dropdown-without-margin` : `${styles.root} dropdown`,
        widthClass,
        classes.root,
        className,
    ]
        .filter(Boolean)
        .join(" ");

    const optionsWithoutUndefined = options === undefined ? [] : options;

    // Options may be grouped under headings. Everything below reasons about the flat list:
    // a group is a display device, not a level of the value space.
    const isGroup = (option: DropdownOptionOrGroup): option is { label: string; options: DropdownOption[] } =>
        Array.isArray((option as { options?: unknown }).options);
    const flatOptions: DropdownOption[] = optionsWithoutUndefined.flatMap((option) =>
        isGroup(option) ? option.options : [option],
    );

    // Map empty form value to an explicit "__none__" option when present (avoids broken empty-string select values)
    const resolvedValue = value === "" && flatOptions.some((o) => o.value === "__none__") ? "__none__" : value;
    const selectedOption = flatOptions.find((option) => option.value === resolvedValue) || null;

    const handleChange = (selected: { value: string; label: string } | null) => {
        if (!selected || selected.value === "__none__") {
            onChange("");
        } else {
            onChange(selected.value);
        }
    };

    const filterOption = (option: { value: string; label: string }, rawInput: string) => {
        const q = rawInput.trim().toLowerCase();
        if (!q) return true;
        return option.label.toLowerCase().includes(q);
    };

    return (
        <div className={rootClass} ref={containerRef} data-rw-control="dropdown">
            {label && <label>{label}</label>}

            <div
                className={[styles.controlWrap, "dropdown-control-wrap", classes.controlWrap].filter(Boolean).join(" ")}
            >
                {error ? (
                    <p className={`${styles.errorMessage} error-message`}>{t("errorLoadingOptions")}</p>
                ) : (
                    <Select
                        options={optionsWithoutUndefined as any}
                        value={selectedOption}
                        onChange={handleChange}
                        isDisabled={disabled || loading}
                        isLoading={loading}
                        placeholder={placeholder}
                        isSearchable
                        isClearable={isClearable}
                        filterOption={filterOption}
                        classNamePrefix="rw-select"
                        className={isMissing ? `input-dropdown-error ${styles.inputError}` : ""}
                        styles={getCustomStyles(isMissing)}
                        onBlur={onBlur}
                        name={name}
                        menuPlacement={placement}
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        maxMenuHeight={MENU_MAX_HEIGHT}
                        onMenuOpen={handleMenuOpen}
                        menuShouldBlockScroll={false}
                        noOptionsMessage={() => t("noOptions") || "No options"}
                    />
                )}
            </div>
        </div>
    );
};

export default Dropdown;
